import { useCallback, useContext, useEffect, useState } from 'react';

import * as EmpresaAPI from '../../../api/empresa';
import * as UnidadeCondominialProdutoAPI from '../../../api/unidadeCondominialProduto';
import { Context } from '../../../context/UserContext';
import { getEmpresaScope } from '../../../helpers/sessionEmpresa';
import { useNavigateParams } from '../../../hooks/useNavigateParams';
import useFlashMessage from '../../../hooks/useFlashMessage';
import styles from '../../form/Form.module.css';

import Form from './Form';

function obterDataAtual() {
    const agora = new Date();

    const dataLocal = new Date(agora.getTime() - agora.getTimezoneOffset() * 60 * 1000);

    return dataLocal.toISOString().slice(0, 10);
}

function criarEstadoInicial(empresaId) {
    return {
        empresaId: Number(empresaId) || '',
        unidadeCondominialId: null,
        produtoId: null,
        tabelaPrecoId: null,
        embalagemId: null,

        UnidadeCondominial: {
            descricao: '',
        },

        tabelaPreco: {
            descricao: '',
        },

        Produto: {
            descricao: '',
        },

        Embalagem: {
            descricao: '',
        },

        ServicoTabela: {
            descricao: '',
        },

        quantidade: 1,
        dataInicio: obterDataAtual(),
        dataFim: '',
        ativo: true,
        observacao: '',

        aceitaFracao: false,
        precoVigente: null,
    };
}

function montarDescricaoUnidade(unidade) {
    if (!unidade) return '';

    return [unidade.codigo, unidade.descricaoOriginal || unidade.descricao].filter(Boolean).join(' — ');
}

function montarServicoFallback(vinculo) {
    const produto = vinculo?.Produto;
    const embalagem = vinculo?.Embalagem;

    if (!produto?.descricao) {
        return {
            descricao: '',
        };
    }

    return {
        produtoId: vinculo.produtoId,
        embalagemId: vinculo.embalagemId,
        codigo: produto.codigo,
        produtoDescricao: produto.descricao,
        embalagemDescricao: embalagem?.descricao || '',
        descricao: [produto.codigo, produto.descricao, embalagem?.descricao].filter(Boolean).join(' — '),
        aceitaFracao: false,
        preco: null,
    };
}

function CreateOrEdit() {
    const { parametro } = useContext(Context);
    const { empresaId } = getEmpresaScope();
    const { setFlashMessage } = useFlashMessage();
    const navigate = useNavigateParams();

    const pathname = '/UnidadeCondominialProdutos';
    const urlParams = new URLSearchParams(window.location.search);

    const uuid = urlParams.get('uuid');

    const editando = Boolean(uuid && uuid !== 'null');

    const [token] = useState(localStorage.getItem('token') || '');

    const [dados, setDados] = useState(() => criarEstadoInicial(empresaId));

    const [optionsEmpresa, setOptionsEmpresa] = useState([]);
    const [carregando, setCarregando] = useState(editando);
    const [salvando, setSalvando] = useState(false);
    const [falhaCarregamento, setFalhaCarregamento] = useState(false);

    const carregarVinculo = useCallback(async () => {
        if (!editando || !uuid) {
            return null;
        }

        setCarregando(true);
        setFalhaCarregamento(false);

        try {
            const response = await UnidadeCondominialProdutoAPI.consultar(uuid);

            const vinculo = response.data?.data || response.data;

            let servicoTabela = montarServicoFallback(vinculo);

            try {
                const responseServicos = await UnidadeCondominialProdutoAPI.listarServicosDisponiveis({
                    empresaId: vinculo.empresaId,
                    tabelaPrecoId: vinculo.tabelaPrecoId,
                    filtro: vinculo.Produto?.descricao || '',
                    limite: 100,
                });

                const servicos = responseServicos.data?.data || [];

                const servicoEncontrado = servicos.find(
                    (item) =>
                        Number(item.produtoId) === Number(vinculo.produtoId) &&
                        Number(item.embalagemId) === Number(vinculo.embalagemId)
                );

                if (servicoEncontrado) {
                    servicoTabela = servicoEncontrado;
                }
            } catch (error) {
                console.error('Não foi possível obter o preço vigente do serviço:', error);
            }

            setDados((anterior) => ({
                ...anterior,
                ...vinculo,

                empresaId: Number(vinculo.empresaId) || '',

                unidadeCondominialId: Number(vinculo.unidadeCondominialId) || null,

                produtoId: Number(vinculo.produtoId) || null,

                tabelaPrecoId: Number(vinculo.tabelaPrecoId) || null,

                embalagemId: Number(vinculo.embalagemId) || null,

                UnidadeCondominial: {
                    ...(vinculo.UnidadeCondominial || {}),
                    descricao: montarDescricaoUnidade(vinculo.UnidadeCondominial),
                },

                tabelaPreco: vinculo.tabelaPreco || {
                    descricao: '',
                },

                Produto: vinculo.Produto || {
                    descricao: '',
                },

                Embalagem: vinculo.Embalagem || {
                    descricao: '',
                },

                ServicoTabela: servicoTabela,

                quantidade: vinculo.quantidade ?? 1,

                dataInicio: vinculo.dataInicio ? String(vinculo.dataInicio).slice(0, 10) : '',

                dataFim: vinculo.dataFim ? String(vinculo.dataFim).slice(0, 10) : '',

                ativo: Boolean(vinculo.ativo),

                observacao: vinculo.observacao || '',

                aceitaFracao: Boolean(servicoTabela?.aceitaFracao),

                precoVigente: servicoTabela?.preco ?? null,
            }));

            return vinculo;
        } catch (error) {
            setFalhaCarregamento(true);

            setFlashMessage(
                error.response?.data?.message || 'Não foi possível carregar o serviço da unidade condominial.',
                error.response?.data?.type || 'error'
            );

            return null;
        } finally {
            setCarregando(false);
        }
    }, [editando, setFlashMessage, uuid]);

    const consultarEmpresas = useCallback(async () => {
        try {
            const result = await EmpresaAPI.empresa();

            setOptionsEmpresa(result);

            return result;
        } catch (error) {
            setOptionsEmpresa([]);

            setFlashMessage(
                error.response?.data?.message || 'Não foi possível consultar as empresas.',
                error.response?.data?.type || 'error'
            );

            return [];
        }
    }, [setFlashMessage]);

    useEffect(() => {
        consultarEmpresas();
    }, [consultarEmpresas]);

    useEffect(() => {
        if (!token) {
            navigate('/Auth', {
                replace: true,
            });
        }
    }, [navigate, token]);

    useEffect(() => {
        carregarVinculo();
    }, [carregarVinculo]);

    async function salvarVinculo(novosDados) {
        if (salvando) {
            return;
        }

        const empresaIdSelecionada = Number(novosDados.empresaId);

        const unidadeCondominialId = Number(novosDados.unidadeCondominialId);

        const produtoId = Number(novosDados.produtoId);

        const tabelaPrecoId = Number(novosDados.tabelaPrecoId);

        const embalagemId = Number(novosDados.embalagemId);

        if (!Number.isInteger(empresaIdSelecionada) || empresaIdSelecionada <= 0) {
            setFlashMessage('Selecione uma empresa válida.', 'warning');

            return;
        }

        if (!Number.isInteger(unidadeCondominialId) || unidadeCondominialId <= 0) {
            setFlashMessage('Selecione uma unidade condominial válida.', 'warning');

            return;
        }

        if (!Number.isInteger(tabelaPrecoId) || tabelaPrecoId <= 0) {
            setFlashMessage('Selecione uma tabela de preços válida.', 'warning');

            return;
        }

        if (!Number.isInteger(produtoId) || produtoId <= 0) {
            setFlashMessage('Selecione um serviço válido.', 'warning');

            return;
        }

        if (!Number.isInteger(embalagemId) || embalagemId <= 0) {
            setFlashMessage('A embalagem do serviço não foi identificada.', 'warning');

            return;
        }

        const quantidadeTexto = String(novosDados.quantidade ?? '')
            .trim()
            .replace(',', '.');

        if (!/^\d+(\.\d{1,3})?$/.test(quantidadeTexto)) {
            setFlashMessage('A quantidade deve possuir no máximo três casas decimais.', 'warning');

            return;
        }

        const quantidade = Number(quantidadeTexto);

        if (!Number.isFinite(quantidade) || quantidade <= 0) {
            setFlashMessage('A quantidade deve ser maior que zero.', 'warning');

            return;
        }

        if (quantidade > 9999999.999) {
            setFlashMessage('A quantidade excede o limite permitido.', 'warning');

            return;
        }

        const aceitaFracao = Boolean(novosDados.aceitaFracao ?? novosDados.ServicoTabela?.aceitaFracao);

        if (!aceitaFracao && !Number.isInteger(quantidade)) {
            setFlashMessage('A embalagem selecionada não aceita quantidade fracionada.', 'warning');

            return;
        }

        const dataInicio = String(novosDados.dataInicio || '').trim();

        const dataFim = String(novosDados.dataFim || '').trim() || null;

        if (!dataInicio) {
            setFlashMessage('Informe a data de início da vigência.', 'warning');

            return;
        }

        if (dataFim && dataFim < dataInicio) {
            setFlashMessage('A data de término não pode ser anterior à data de início.', 'warning');

            return;
        }

        const dadosAlteraveis = {
            quantidade,
            dataInicio,
            dataFim,
            ativo: Boolean(novosDados.ativo),
            observacao: String(novosDados.observacao || '').trim() || null,
        };

        const payload = editando
            ? dadosAlteraveis
            : {
                  empresaId: empresaIdSelecionada,
                  unidadeCondominialId,
                  produtoId,
                  tabelaPrecoId,
                  embalagemId,
                  ...dadosAlteraveis,
              };

        setSalvando(true);

        try {
            const response = editando
                ? await UnidadeCondominialProdutoAPI.editar(uuid, payload)
                : await UnidadeCondominialProdutoAPI.cadastrar(payload);

            setFlashMessage(response.data.message, response.data.type);

            const sucesso = response.status >= 200 && response.status < 300 && response.data?.type === 'success';

            if (!sucesso) {
                return;
            }

            const params = new URLSearchParams(urlParams);

            params.delete('uuid');
            params.delete('btnText');

            navigate(pathname, params);
        } catch (error) {
            setFlashMessage(
                error.response?.data?.message || error.message || 'Não foi possível salvar o serviço da unidade condominial.',
                error.response?.data?.type || 'error'
            );
        } finally {
            setSalvando(false);
        }
    }

    return (
        <section className={`${styles.form_container} ${styles[parametro.tema]}`}>
            <Form
                dados={dados}
                setDados={setDados}
                handleSubmit={salvarVinculo}
                caminhoVoltar={pathname}
                OptionsEmpresa={optionsEmpresa}
                editando={editando}
                carregando={carregando}
                salvando={salvando}
                falhaCarregamento={falhaCarregamento}
            />
        </section>
    );
}

export default CreateOrEdit;
